import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminApiError } from '../../api/admin-client.js';
import {
  fetchAdminCustomers,
  fetchAdminCustomerProfile,
  updateAdminCustomerStatus,
} from '../api/customers-api.js';
import type { CustomerListParams, UpdateCustomerStatusInput } from '../types/customers.js';

const CUSTOMERS_KEY = ['admin', 'customers'] as const;

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new AdminApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

function nonRetryableStatus(error: unknown): boolean {
  return error instanceof AdminApiError && [401, 403, 404, 422].includes(error.status);
}

export function useAdminCustomers(params: CustomerListParams) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: [...CUSTOMERS_KEY, 'list', params] as const,
    queryFn: async ({ signal }) => fetchAdminCustomers(params, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 15_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

export function useAdminCustomerProfile(customerId: string | undefined) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: [...CUSTOMERS_KEY, 'detail', customerId ?? ''] as const,
    queryFn: async ({ signal }) =>
      fetchAdminCustomerProfile(customerId!, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true && Boolean(customerId),
    staleTime: 10_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

export function useAdminCustomerStatusMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, input }: { customerId: string; input: UpdateCustomerStatusInput }) =>
      updateAdminCustomerStatus(customerId, input, { token: await requireToken(getToken) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CUSTOMERS_KEY });
    },
  });
}
